import api from './api';

export interface ProductSubscription {
  id: string;
  orgId: string;
  productId: string;
  subscribedByUserId: string;
  startDate: string;
  endDate: string | null;
  isActive: boolean;
  subscribedAt: string;
  isCurrentlyValid: boolean;
}

export interface CreateSubscriptionRequest {
  orgId: string;
  productId: string;
  startDate: string;
  endDate?: string | null;
}

export interface UpdateSubscriptionRequest {
  startDate: string;
  endDate?: string | null;
}

/** Flattened subscribed-product row returned by `GET /subscriptions/organizations/{orgId}/products`.
 *  Backend joins the publisher org's name in so subscribers can tell who published each product. */
export interface SubscribedProduct {
  id: string;
  name: string;
  description: string;
  orgId: string;
  publisherOrgName: string;
  masterProjectId: string;
  hasPublishedVersion: boolean;
}

export interface SubscriberRunMetric {
  orgId: string;
  totalRuns: number;
  /** ISO timestamp of the most recent completed run, or null if no runs recorded. */
  lastRun: string | null;
}

/** Subscriber row returned by `GET /subscriptions/products/{productId}/subscribers` — joins
 *  the subscribing org's display name and primary contact email in for the publisher UI. */
export interface ProductSubscriber {
  id: string;
  orgId: string;
  orgName: string;
  contactEmail: string;
  productId: string;
  startDate: string;
  endDate: string | null;
  isActive: boolean;
  subscribedAt: string;
}

export const subscriptionsApi = {
  /**
   * Get all subscribers for a specific product
   */
  async getProductSubscribers(productId: string): Promise<ProductSubscriber[]> {
    const response = await api.get(`/subscriptions/products/${productId}/subscribers`);
    if (!response.ok) {
      throw new Error('Failed to fetch product subscribers');
    }
    return response.json();
  },

  /**
   * Per-subscriber run metrics for a product. Returns one row per subscribing org.
   * Backed by RunHistory rows tagged with RunningProjectId at run start; runs recorded
   * before the field was added show as zero counts.
   */
  async getProductSubscriberMetrics(productId: string): Promise<SubscriberRunMetric[]> {
    const response = await api.get(`/subscriptions/products/${productId}/subscriber-metrics`);
    if (!response.ok) {
      throw new Error('Failed to fetch product subscriber metrics');
    }
    return response.json();
  },

  /**
   * Get all subscriptions for a specific organization
   */
  async getOrgSubscriptions(orgId: string): Promise<ProductSubscription[]> {
    const response = await api.get(`/subscriptions/organizations/${orgId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch organization subscriptions');
    }
    return response.json();
  },

  /**
   * Get all products that an organization is subscribed to
   */
  async getOrgSubscribedProducts(orgId: string): Promise<SubscribedProduct[]> {
    const response = await api.get(`/subscriptions/organizations/${orgId}/products`);
    if (!response.ok) {
      throw new Error('Failed to fetch subscribed products');
    }
    return response.json();
  },

  /**
   * Create a new product subscription
   */
  async createSubscription(data: CreateSubscriptionRequest): Promise<ProductSubscription> {
    const response = await api.post('/subscriptions', data);
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to create subscription');
    }
    return response.json();
  },

  /**
   * Update an existing subscription
   */
  async updateSubscription(
    subscriptionId: string,
    data: UpdateSubscriptionRequest
  ): Promise<ProductSubscription> {
    const response = await api.put(`/subscriptions/${subscriptionId}`, data);
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to update subscription');
    }
    return response.json();
  },

  /**
   * Remove/Deactivate a subscription
   */
  async deactivateSubscription(subscriptionId: string): Promise<void> {
    const response = await api.delete(`/subscriptions/${subscriptionId}`);
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to remove subscription');
    }
  },
};
